import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import RatingCell from "@/components/RatingCell";
import ListDialog from "@/components/ListDialog";
import OpponentPicker from "@/components/OpponentPicker";
import { TIERS, effectiveCell, fmtScore, factionIcon } from "@/lib/game";
import BreakdownLabel from "@/components/BreakdownLabel";
import { api } from "@/lib/api";
import { Grid3x3, TrendingUp, Trophy, Target, RotateCcw, CheckCircle2, Lock, Unlock } from "lucide-react";
import { useTeam } from "@/lib/team";

export default function MatrixTab({ setup, updateSetup }) {
  const { activeId } = useTeam();
  const yours = setup.players.filter((p) => p.team === setup.you_are);
  const opp = setup.players.filter((p) => p.team !== setup.you_are);
  const [analysis, setAnalysis] = useState(null);
  const [err, setErr] = useState("");

  const sig = useMemo(
    () => JSON.stringify([setup.matrix, setup.faction_ratings, setup.players.map((p) => [p.id, p.team, p.faction]), setup.you_are]),
    [setup]
  );

  useEffect(() => {
    let active = true;
    const t = setTimeout(() => {
      api.teamAnalyze(activeId, null)
        .then((r) => active && (setAnalysis(r), setErr("")))
        .catch((e) => active && setErr(e?.response?.data?.detail || "Setup incomplete"));
    }, 300);
    return () => { active = false; clearTimeout(t); };
  }, [sig, activeId]);

  const setOverride = (yId, oId, val) =>
    updateSetup({ ...setup, matrix: { ...setup.matrix, [`${yId}|${oId}`]: val } });
  const clearOverride = (yId, oId) => {
    const m = { ...setup.matrix };
    delete m[`${yId}|${oId}`];
    updateSetup({ ...setup, matrix: m });
  };
  const resetMatrix = () => {
    if (!window.confirm("Reset this opponent's matrix to the Little-Win defaults (clears per-game overrides & their notes)?")) return;
    updateSetup({ ...setup, matrix: {} });
    toast.success("Matrix reset to defaults");
  };

  if (yours.length === 0 || opp.length === 0) {
    return (
      <div className="space-y-6">
        <OpponentPicker setup={setup} updateSetup={updateSetup} />
        <Card className="bg-[#121824]/70 border-slate-800 p-10 text-center">
          <Grid3x3 className="h-8 w-8 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">{yours.length === 0 ? <>Claim your team in <b>My Team</b> first.</> : <>Pick an opponent above to build the matrix.</>}</p>
        </Card>
      </div>
    );
  }

  const proj = analysis?.projection;
  const best = analysis?.best_case;

  return (
    <div className="space-y-6">
      <OpponentPicker setup={setup} updateSetup={updateSetup} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="bg-[#121824]/70 border-cyan-700/30 p-4 lg:col-span-2">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="h-4 w-4 text-cyan-400" />
            <h3 className="font-display font-bold uppercase tracking-wide text-sm">Projected Wins</h3>
          </div>
          {err ? (
            <p className="text-sm text-amber-400/80 mt-2">{err} — both teams need the same number of players.</p>
          ) : proj ? (
            <div className="flex flex-wrap items-stretch gap-4 mt-2">
              <div className="rounded-xl border border-emerald-600/40 bg-emerald-950/20 px-5 py-3" data-testid="best-case-box">
                <p className="text-[11px] text-emerald-300/80 uppercase tracking-wider flex items-center gap-1"><Trophy className="h-3 w-3" /> Best case</p>
                <p className="font-mono text-3xl font-bold text-emerald-400">{best?.wins}<span className="text-slate-600 text-xl">–{best?.losses}</span></p>
                <p className="text-[11px] text-slate-500">if pairings fall your way · <BreakdownLabel breakdown={best?.breakdown} /></p>
              </div>
              <div className="rounded-xl border border-amber-600/40 bg-amber-950/20 px-5 py-3" data-testid="predicted-box">
                <p className="text-[11px] text-amber-300/80 uppercase tracking-wider flex items-center gap-1"><Target className="h-3 w-3" /> Predicted</p>
                <p className="font-mono text-3xl font-bold text-amber-400">{proj.wins}<span className="text-slate-600 text-xl">–{proj.losses}</span></p>
                <p className="text-[11px] text-slate-500">if opponent counter-picks optimally · <BreakdownLabel breakdown={proj.breakdown} /></p>
              </div>
              {analysis?.side && (
                <div className="rounded-xl border border-slate-700 bg-slate-900/40 px-5 py-3 flex flex-col justify-center">
                  <p className="text-[11px] text-slate-500 uppercase tracking-wider">Pick side (dice-off)</p>
                  <p className="font-mono text-xl font-bold text-cyan-400">Team {analysis.side.recommend}</p>
                  <p className="text-[11px] text-slate-500">A {analysis.side.wins_as_a}–{analysis.side.losses_as_a} · B {analysis.side.wins_as_b}–{analysis.side.losses_as_b}</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-500 mt-2">Calculating…</p>
          )}
        </Card>
        <Card className="bg-[#121824]/70 border-slate-800 p-4">
          <p className="text-[11px] text-slate-500 uppercase tracking-wider mb-2">Tier legend</p>
          <div className="grid grid-cols-2 gap-1.5">
            {TIERS.map((t) => (
              <div key={t.key} className="flex items-center gap-2 text-xs rounded px-2 py-1" style={{ background: t.bg, color: t.text }}>
                <span className="font-mono font-bold">{t.code}</span>{t.label}
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 mt-2">Dashed = default from faction rating · solid = edited here</p>
        </Card>
      </div>

      <Card className="bg-[#121824]/70 border-slate-800 p-3 sm:p-5 overflow-x-auto">
        {opp.length > 0 && yours.length > 0 && (() => {
          const readyCount = yours.filter((y) => opp.every((o) => effectiveCell(setup, y.id, o.id, o.faction).source !== "fallback")).length;
          const allReady = readyCount === yours.length;
          return (
            <div className={`mb-3 rounded-lg border px-3 py-2 flex items-center gap-2 text-sm ${allReady ? "border-emerald-600/40 bg-emerald-950/20 text-emerald-300" : "border-amber-600/30 bg-amber-950/10 text-amber-300"}`}
              data-testid="readiness-summary">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              {allReady
                ? "Grid complete — all players have rated this opponent."
                : `Readiness: ${readyCount}/${yours.length} players have fully rated this opponent.`}
            </div>
          );
        })()}
        <div className="flex items-start justify-between gap-3 mb-3">
          <p className="text-xs text-slate-500 flex-1">
            Rows = your players. Columns = this opponent (click a name to view their lists). Every cell starts at Little Win; click a cell to override or add a note for this game.
          </p>
          <div className="flex items-center gap-2 shrink-0">
            <Button data-testid="matrix-lock-opponent-button" onClick={() => updateSetup({ ...setup, opponent_locked: !setup.opponent_locked })} variant="outline" size="sm"
              className={setup.opponent_locked ? "border-amber-500/60 bg-amber-500/15 text-amber-300" : "border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800"}>
              {setup.opponent_locked ? <><Lock className="h-3.5 w-3.5 mr-1.5" /> Opponent locked</> : <><Unlock className="h-3.5 w-3.5 mr-1.5" /> Lock opponent</>}
            </Button>
            <Button data-testid="reset-matrix-button" onClick={resetMatrix} variant="outline" size="sm"
              className="border-slate-700 bg-transparent text-slate-300 hover:text-red-400">
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Reset
            </Button>
          </div>
        </div>
        <div className="min-w-[640px]">
          <div className="grid gap-2" style={{ gridTemplateColumns: `180px repeat(${opp.length}, minmax(80px, 1fr))` }}>
            <div />
            {opp.map((o) => (
              <ListDialog key={o.id} player={o}>
                <button data-testid="matrix-opp-list-trigger" className="flex flex-col items-center pb-1 group">
                  <img src={factionIcon(o.faction)} alt={o.faction} className="h-6 w-6 object-contain mb-0.5 transition-transform group-hover:scale-110"
                    onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  <p className="text-xs font-semibold text-cyan-300 truncate max-w-full group-hover:text-cyan-200 group-hover:underline">{o.name || "—"}</p>
                  <p className="text-[10px] text-slate-500 font-mono truncate max-w-full">{o.faction}</p>
                </button>
              </ListDialog>
            ))}
            {yours.map((y) => (
              <MatrixRow key={y.id} y={y} opp={opp} setup={setup} setOverride={setOverride} clearOverride={clearOverride} />
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}

function MatrixRow({ y, opp, setup, setOverride, clearOverride }) {
  const rated = opp.filter((o) => effectiveCell(setup, y.id, o.id, o.faction).source !== "fallback").length;
  const ready = opp.length > 0 && rated === opp.length;
  return (
    <>
      <div className="flex flex-col justify-center pr-2">
        <div className="flex items-center gap-1.5">
          {ready
            ? <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" data-testid="player-ready-tick" />
            : <span className="text-[10px] font-mono text-amber-400/80 bg-amber-500/10 rounded px-1 shrink-0" data-testid="player-ready-progress">{rated}/{opp.length}</span>}
          <p className="text-sm font-semibold text-amber-300 truncate">{y.name || "—"}</p>
        </div>
        <p className="text-[10px] text-slate-500 font-mono truncate pl-5">{y.faction}</p>
      </div>
      {opp.map((o) => (
        <RatingCell
          key={o.id}
          testid="matrix-cell-select"
          dashedWhenDefault
          armyName={o.faction}
          cellId={`${y.id}|${o.id}`}
          cell={effectiveCell(setup, y.id, o.id, o.faction)}
          onSet={(val) => setOverride(y.id, o.id, val)}
          onClear={() => clearOverride(y.id, o.id)}
        />
      ))}
    </>
  );
}
